import { sectionRenderer } from '@theme/section-renderer';

const TAB_SELECTOR = '[data-ciara-collection-tab]';
const ROOT_SELECTOR = '[data-ciara-collection-root]';
const INITIALIZED_KEY = '__ciaraCollectionTabsSafeInitialized';

if (!window[INITIALIZED_KEY]) {
  window[INITIALIZED_KEY] = true;

  let isRendering = false;
  let queuedRequest = null;

  /**
   * Find the live Main Collection section.
   *
   * @param {Element | null} source
   * @returns {{ root: HTMLElement, section: HTMLElement, sectionId: string } | null}
   */
  const getContext = (source = null) => {
    const root =
      source?.closest(ROOT_SELECTOR) ||
      document.querySelector(ROOT_SELECTOR);

    if (!(root instanceof HTMLElement)) return null;

    const section = root.closest('[id^="shopify-section-"]');
    if (!(section instanceof HTMLElement)) return null;

    const sectionId =
      root.dataset.sectionId ||
      section.id.replace(/^shopify-section-/, '');

    if (!sectionId) return null;

    return { root, section, sectionId };
  };

  /**
   * Toggle a stable loading state without changing the section dimensions.
   *
   * @param {HTMLElement | null} section
   * @param {boolean} loading
   */
  const setLoading = (section, loading) => {
    if (!section) return;

    const value = loading ? 'true' : 'false';

    section.querySelector('results-list')?.setAttribute('aria-busy', value);
    section
      .querySelector('.ciara-collection-tabs-wrapper')
      ?.setAttribute('aria-busy', value);

    section.querySelectorAll(TAB_SELECTOR).forEach((tab) => {
      if (tab instanceof HTMLButtonElement) {
        tab.disabled = loading;
      }
    });
  };

  /**
   * Update the visible active state before the response arrives.
   * The server-rendered state replaces this after the section render.
   *
   * @param {HTMLElement} section
   * @param {HTMLButtonElement | null} activeTab
   */
  const setOptimisticActiveTab = (section, activeTab) => {
    if (!activeTab) return;

    section.querySelectorAll(TAB_SELECTOR).forEach((tab) => {
      const isActive = tab === activeTab;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-pressed', isActive ? 'true' : 'false');

      if (isActive) {
        tab.setAttribute('aria-current', 'page');
      } else {
        tab.removeAttribute('aria-current');
      }
    });
  };

  /**
   * Set the page title from the newly server-rendered active tab.
   *
   * @param {HTMLElement | null} section
   */
  const updateDocumentTitle = (section) => {
    const activeTab = section?.querySelector(
      `${TAB_SELECTOR}[aria-current="page"]`
    );

    if (
      activeTab instanceof HTMLElement &&
      activeTab.dataset.documentTitle
    ) {
      document.title = activeTab.dataset.documentTitle;
    }
  };

  /**
   * Wait until the browser has completed one paint after Horizon morphs the section.
   * This lets reconnected custom elements rebuild their refs before another request.
   */
  const waitForPaint = () =>
    new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });

  /**
   * Render the selected collection through Horizon's Section Rendering API.
   * The URL is changed BEFORE rendering, matching Horizon's native facets flow.
   *
   * @param {URL} targetUrl
   * @param {{ historyMode?: 'push' | 'none', source?: HTMLButtonElement | null, restoreFocus?: boolean }} options
   */
  const renderCollection = async (
    targetUrl,
    { historyMode = 'push', source = null, restoreFocus = false } = {}
  ) => {
    if (isRendering) {
      queuedRequest = { targetUrl, historyMode, source, restoreFocus };
      return;
    }

    const context = getContext(source);
    if (!context) {
      console.error('[Ciara collection tabs] Main Collection section was not found.');
      return;
    }

    const cleanUrl = new URL(targetUrl.toString());
    cleanUrl.hash = '';
    cleanUrl.searchParams.delete('section_id');
    cleanUrl.searchParams.delete('sections');
    cleanUrl.searchParams.delete('page');

    const previousUrl = new URL(window.location.href);
    const previousState = history.state;
    const previousHandle = context.root.dataset.collectionHandle || '';
    const previousRenderedUrl = new URL(
      context.root.dataset.collectionUrl || previousUrl.toString(),
      window.location.origin
    );

    isRendering = true;
    queuedRequest = null;
    setLoading(context.section, true);
    setOptimisticActiveTab(context.section, source);

    // Horizon's native facets code changes history before rendering. Doing the
    // same prevents newly connected components from reading the old collection URL.
    if (historyMode === 'push') {
      history.pushState(
        {
          ...(history.state && typeof history.state === 'object' ? history.state : {}),
          ciaraCollectionUrl: cleanUrl.toString(),
        },
        '',
        `${cleanUrl.pathname}${cleanUrl.search}`
      );
    }

    try {
      // Use the current browser URL, exactly as Horizon's own facets renderer does.
      await sectionRenderer.renderSection(context.sectionId, {
        cache: false,
      });

      await waitForPaint();

      const updatedContext = getContext();
      if (!updatedContext) {
        throw new Error('The updated Main Collection section was not found.');
      }

      const expectedHandle = source?.dataset.collectionHandle || '';
      const actualHandle = updatedContext.root.dataset.collectionHandle || '';

      if (expectedHandle && actualHandle && expectedHandle !== actualHandle) {
        throw new Error(
          `Collection mismatch: expected "${expectedHandle}" but received "${actualHandle}".`
        );
      }

      updateDocumentTitle(updatedContext.section);

      if (restoreFocus) {
        const activeTab = updatedContext.section.querySelector(
          `${TAB_SELECTOR}[aria-current="page"]`
        );

        if (activeTab instanceof HTMLButtonElement) {
          activeTab.focus({ preventScroll: true });
        }
      }

      document.dispatchEvent(
        new CustomEvent('ciara:collection-tabs:loaded', {
          detail: {
            url: cleanUrl.toString(),
            sectionId: context.sectionId,
            collectionHandle: updatedContext.root.dataset.collectionHandle || '',
          },
        })
      );
    } catch (error) {
      console.error('[Ciara collection tabs] AJAX render failed.', error);

      // Restore the address bar when the AJAX request fails. This does not reload the page.
      const rollbackUrl = historyMode === 'push' ? previousUrl : previousRenderedUrl;

      history.replaceState(
        previousState,
        '',
        `${rollbackUrl.pathname}${historyMode === 'push' ? rollbackUrl.search : ''}${rollbackUrl.hash}`
      );

      // A validation error can happen after Horizon has already morphed the section.
      // In that case, render the previously displayed collection back into the same section.
      const rollbackContext = getContext();
      const rollbackHandle = rollbackContext?.root.dataset.collectionHandle || '';

      if (
        rollbackContext &&
        previousHandle &&
        rollbackHandle &&
        rollbackHandle !== previousHandle
      ) {
        try {
          await sectionRenderer.renderSection(context.sectionId, {
            cache: false,
          });
          await waitForPaint();
        } catch (rollbackError) {
          console.error(
            '[Ciara collection tabs] Could not restore the previous collection.',
            rollbackError
          );
        }
      }

      document.dispatchEvent(
        new CustomEvent('ciara:collection-tabs:error', {
          detail: {
            url: cleanUrl.toString(),
            sectionId: context.sectionId,
            error,
          },
        })
      );
    } finally {
      const latestContext = getContext();
      setLoading(latestContext?.section || context.section, false);
      isRendering = false;

      if (queuedRequest) {
        const nextRequest = queuedRequest;
        queuedRequest = null;

        // The queued button may have been replaced by the previous morph. Resolve
        // the equivalent live button from its collection handle or URL.
        const liveContext = getContext();
        const requestedHandle = nextRequest.source?.dataset.collectionHandle || '';
        const requestedPath = `${nextRequest.targetUrl.pathname}${nextRequest.targetUrl.search}`;

        const liveSource = Array.from(
          liveContext?.section.querySelectorAll(TAB_SELECTOR) || []
        ).find((tab) => {
          if (!(tab instanceof HTMLButtonElement)) return false;

          if (requestedHandle && tab.dataset.collectionHandle === requestedHandle) {
            return true;
          }

          const tabUrl = new URL(
            tab.dataset.collectionUrl || '',
            window.location.origin
          );

          return `${tabUrl.pathname}${tabUrl.search}` === requestedPath;
        });

        renderCollection(nextRequest.targetUrl, {
          historyMode: nextRequest.historyMode,
          source: liveSource instanceof HTMLButtonElement ? liveSource : null,
          restoreFocus: nextRequest.restoreFocus,
        });
      }
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      const tab = event.target.closest(TAB_SELECTOR);
      if (!(tab instanceof HTMLButtonElement)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const rawUrl = tab.dataset.collectionUrl;
      if (!rawUrl || tab.disabled) return;

      const targetUrl = new URL(rawUrl, window.location.origin);
      if (targetUrl.origin !== window.location.origin) return;

      const currentUrl = new URL(window.location.href);
      if (
        targetUrl.pathname === currentUrl.pathname &&
        targetUrl.search === currentUrl.search
      ) {
        return;
      }

      renderCollection(targetUrl, {
        historyMode: 'push',
        source: tab,
        restoreFocus: true,
      });
    },
    true
  );

  window.addEventListener('popstate', () => {
    renderCollection(new URL(window.location.href), {
      historyMode: 'none',
      source: null,
      restoreFocus: false,
    });
  });
}
